import * as React from "react";
import { useNavigate, useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";

/** Which detail route, if any, an entity of this type has. */
const detailRouteSegment: Partial<Record<MigrationType, string>> = {
  [MigrationType.CasesMigration]: "case",
  [MigrationType.CaseFoldersMigration]: "case",
  [MigrationType.IntakesMigration]: "intake",
  [MigrationType.ProvidersMigration]: "provider",
  [MigrationType.ProviderFoldersMigration]: "provider",
};

interface MigrationEntityCellProps {
  entityType?: MigrationType;
  migrationEntityId?: string;
  entityLabel?: string;
}

/**
 * The entity a finding belongs to.
 *
 * Findings carry no foreign key, so three outcomes are all normal and must read differently: no entity was ever
 * attributed, one was but no longer exists, and one exists and can be opened. Collapsing the middle case into a
 * blank cell would make a deleted parent look like a reporting bug.
 */
export const MigrationEntityCell: React.FC<MigrationEntityCellProps> = ({
  entityType,
  migrationEntityId,
  entityLabel,
}) => {
  const navigate = useNavigate();
  const { organizationId } = useParams();

  if (!entityType || !migrationEntityId) {
    return <span className="text-muted-foreground italic">Unattributed</span>;
  }

  const typeBadge = (
    <Badge variant="neutral">{MigrationTypeLabels[entityType] ?? entityType}</Badge>
  );

  if (!entityLabel) {
    return (
      <div className="flex items-center gap-2">
        {typeBadge}
        <span className="text-muted-foreground italic">No longer exists</span>
      </div>
    );
  }

  const segment = detailRouteSegment[entityType];

  return (
    <div className="flex items-center gap-2 min-w-0">
      {typeBadge}
      {segment && organizationId ? (
        <Button
          variant="link"
          className="h-auto p-0 truncate"
          onClick={() => navigate(`/organization/${organizationId}/${segment}/${migrationEntityId}`)}
        >
          {entityLabel}
        </Button>
      ) : (
        <span className="truncate">{entityLabel}</span>
      )}
    </div>
  );
};

export default MigrationEntityCell;
